import { BaseQueryApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query';
import { getAuthTokens } from './helpers/authHelper';
import { PAGES_ENUM } from '../routes';
import { sentryErrorCapture } from './helpers/sentryErrorCapture';
import { setSubscriptionInfo } from 'src/redux/Auth';
import { redirectTo } from './navigationManager';

enum Headers {
  Authorization = 'authorization',
  Accept = 'Accept',
}
export const getTenant = () =>
  process.env.NODE_ENV !== 'development'
    ? window.location.hostname.split('.')[0]
    : process.env.REACT_APP_DEV_TENANT;

let isRedirecting = false;

export const getBaseUrl = (isForGoLang?: boolean) => {
  const hostName = window.location.hostname.split('.');
  return process.env.NODE_ENV !== 'development'
    ? `${window.location.protocol}//${hostName[0]}.${
        isForGoLang ? 'go' : 'api'
      }${process.env.REACT_APP_DOMAIN}/`
    : `https://${process.env.REACT_APP_DEV_TENANT}.${
        isForGoLang ? 'go' : 'api'
      }${process.env.REACT_APP_DOMAIN}/`;
};
const getParamsSerializer = (params: any) => {
  const queryParams = new URLSearchParams();

  for (const param in params) {
    if (Array.isArray(params[param])) {
      params[param].forEach((value: any) => {
        queryParams.append(param, value.toString());
      });
    } else {
      queryParams.set(param, params[param].toString());
    }
  }

  return queryParams.toString();
};
const getPrepareHeader = (headers: any) => {
  const token = getAuthTokens().accessToken;
  if (token) {
    headers.set(Headers.Authorization, `Bearer ${token}`);
  }
  headers.set(Headers.Accept, 'application/json');
  return headers;
};
const fetchBaseQueryErrorHandler = async ({
  args,
  api,
  extraOptions,
  baseQuery,
}: {
  args: any;
  api: BaseQueryApi;
  extraOptions: any;
  baseQuery: any;
}) => {
  const result = await baseQuery(args, api, extraOptions);

  if (
    result?.error?.status &&
    (typeof result?.error?.status !== 'number' || result?.error?.status >= 500)
  ) {
    sentryErrorCapture(result, args); // Capture error for reporting
  }

  const errorMessage = result.error?.data?.message;
  const expectedMessage = 'Subscription has expired';
  const currentPath = window.location.pathname;

  if (
    result.error &&
    result.error.status === 403 &&
    errorMessage === expectedMessage
  ) {
    if (
      ![PAGES_ENUM.SUBSCRIPTIONS as string].includes(currentPath) &&
      !isRedirecting
    ) {
      isRedirecting = true;
      redirectTo(PAGES_ENUM.HOME);
      api.dispatch(setSubscriptionInfo(result?.error?.data));
    }
  }

  // Handle 401 Unauthorized error
  if (result.error && result.error.status === 401) {
    window.location.pathname = PAGES_ENUM.SIGN_IN;
    document.cookie =
      'accessTokenCrm=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  return result;
};

const createBaseQueryWithErrorHandling = (
  baseQuery: BaseQueryFn<
    string | FetchArgs,
    Blob | unknown,
    FetchBaseQueryError
  >
): BaseQueryFn<string | FetchArgs, Blob | unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) =>
    fetchBaseQueryErrorHandler({ args, api, extraOptions, baseQuery });
};

const baseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  paramsSerializer: (params) => getParamsSerializer(params),
  prepareHeaders: (headers) => getPrepareHeader(headers),
});

const baseQueryForGoLang = fetchBaseQuery({
  baseUrl: getBaseUrl(true),
  paramsSerializer: (params) => getParamsSerializer(params),
  prepareHeaders: (headers) => getPrepareHeader(headers),
});

const baseQueryForPython = fetchBaseQuery({
  baseUrl:
    process.env.NODE_ENV !== 'development'
      ? `${getBaseUrl(true)}sse/`
      : ' https://stage.go.stage-profi.site/sse/',
  paramsSerializer: (params) => getParamsSerializer(params),
  prepareHeaders: (headers) => getPrepareHeader(headers),
});

const baseQueryWithReAuth = createBaseQueryWithErrorHandling(baseQuery);

const getBaseQueryForGoLang =
  createBaseQueryWithErrorHandling(baseQueryForGoLang);
const getBaseQueryForPython =
  createBaseQueryWithErrorHandling(baseQueryForPython);

export {
  baseQueryWithReAuth as baseQuery,
  getBaseQueryForGoLang,
  getBaseQueryForPython,
};
